"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Call = void 0;
const tslib_1 = require("tslib");
const lib_1 = require("@video/log-node/lib");
const mobx_1 = require("mobx");
// eslint-disable-next-line import/no-cycle
const api_1 = require("../api");
const broadcast_1 = require("./broadcast");
const errors_1 = require("./errors");
const errors_deprecated_1 = require("./errors-deprecated");
const peer_1 = require("./peer");
const call_1 = tslib_1.__importDefault(require("./pvc/call/call"));
const bind_1 = require("./utils/bind");
const context_1 = require("./utils/context/context");
const event_emitter_1 = require("./utils/events/event-emitter");
const logger_1 = require("./utils/logger");
const events_handler_1 = require("./utils/proxy/events-handler");
const request_1 = tslib_1.__importDefault(require("./utils/request/request"));
const retry_1 = require("./utils/retry");
const auth_core_1 = tslib_1.__importDefault(require("./utils/auth/auth-core"));
const callUrlVersion = "v2";
const RETRY_DELAY = 1000;
const proxyEventsHandler = new events_handler_1.EventsHandler();
class Call extends event_emitter_1.ObservableEventEmitter {
    /**
     * @throws {NoBackendEndpoints} if no backend endpoints are provided
     */
    constructor(ctx, owner, callOptions, sfuJoinParams) {
        super(false);
        this.state = "active";
        this.failover = [];
        this.failoverCallUrls = [];
        this.region = null;
        this.auth = undefined;
        this.pvcCall = null;
        this.connectedPeers = [];
        this.joinUrl = null;
        this.failoverJoinUrls = [];
        this.broadcasts = new Map();
        this.isOwner = false;
        (0, mobx_1.makeObservable)(this, {
            broadcasts: mobx_1.observable,
        });
        this.ctx = ctx;
        (0, context_1.onceCanceled)(ctx).then((reason) => this.dispose(`VideoClient Context Cancelled: ${reason}`));
        const self = (0, bind_1.makeBounded)(new Proxy(this, proxyEventsHandler));
        self.init(owner, callOptions, sfuJoinParams);
        ctx.logger.attachObject(self);
        ctx.logger.trace("constructor()");
        return self;
    }
    init(owner, callOptions, sfuJoinParams) {
        var _a, _b, _c, _d, _e, _f;
        this.isOwner = owner;
        this.options = callOptions;
        if (((_b = (_a = callOptions.backendEndpoints) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0) === 0 && this.options.sfu.joinUrl == null) {
            this.throwError((0, errors_1.createError)(api_1.ErrorCode.NoBackendEndpoints, "backendEndpoints or joinUrl are required", {}));
        }
        this.joinUrl = (_c = this.options.sfu.joinUrl) !== null && _c !== void 0 ? _c : null;
        if (callOptions.backendEndpoints != null) {
            const [backendEndpoint, ...failover] = callOptions.backendEndpoints;
            this.backendEndpoint = backendEndpoint;
            this.failover = failover;
            this.callUrl = `${backendEndpoint}/lb/calls`;
            this.failoverCallUrls = failover.map((lb) => `${lb}/lb/calls`);
            if (this.joinUrl == null) {
                this.joinUrl = `${this.backendEndpoint}/lb/${callUrlVersion}/calls/${this.options.callId}/join`;
                this.failoverJoinUrls = this.failover.map((lb) => `${lb}/lb/${callUrlVersion}/calls/${this.options.callId}/join`);
            }
        }
        if (typeof callOptions.token === "string") {
            this.token = callOptions.token;
        }
        else {
            this.tokenRefresher = callOptions.token;
        }
        if (callOptions.callId != null) {
            this.emit("callId", { callId: callOptions.callId, user: (_e = (_d = this.pvcCall) === null || _d === void 0 ? void 0 : _d.user) !== null && _e !== void 0 ? _e : null });
        }
        this.auth = new auth_core_1.default({
            bootstrap: {
                token: this.token,
                refreshToken: this.tokenRefresher,
            },
        });
        const { callId } = this.options;
        const sfuOptions = (_f = callOptions.sfu) !== null && _f !== void 0 ? _f : {};
        this.readyPromise = (async () => {
            const internalCall = await (0, retry_1.retry)(
            // disabled for now to make sure the player switches to the next driver immediately
            // callOptions.playerOptions?.retryCall ? RETRY_MAX : 0,
            0, RETRY_DELAY, this.createInternalCall.bind(this, callId, callOptions, sfuJoinParams, sfuOptions), this.ctx.logger);
            this.pvcCall = internalCall;
        })();
        this.addInnerDisposer(this.close);
    }
    /**
     * Inits pvcCall and connects to the call
     */
    async createInternalCall(callId, callOptions, sfuJoinParams, sfuOptions, retryCount) {
        var _a, _b;
        let internalCall;
        if (callId == null) {
            if (this.callUrl == null) {
                this.throwError((0, errors_1.createError)(api_1.ErrorCode.NoBackendEndpoints, "backendEndpoints or joinUrl are required", {}));
            }
            internalCall = await this.createCallAndJoin(callOptions, sfuOptions);
        }
        else {
            // Check to see if we want the retry below to fire
            const shouldRetry = (_a = callOptions.playerOptions) === null || _a === void 0 ? void 0 : _a.retryCall;
            if (retryCount > 0 && shouldRetry) {
                // it forces to use loadbalancer if prev try was failed
                sfuJoinParams === null || sfuJoinParams === void 0 ? true : delete sfuJoinParams.call;
            }
            internalCall = await this.joinCall(callId, sfuJoinParams, sfuOptions);
        }
        internalCall.on("error", (err) => {
            this.emit("error", err);
        });
        this.emit("callId", { callId: internalCall.call.id, user: (_b = internalCall.user) !== null && _b !== void 0 ? _b : null });
        internalCall.on("CALL_ADD_PEER", this.handleAddPeer);
        internalCall.on("CALL_REMOVE_PEER", this.handleRemovePeer);
        internalCall.on("CALL_SFU_CONNECTION", () => this.emit("sfuConnection"));
        internalCall.on("CALL_DOMINANT_SPEAKER", (dominantSpeaker) => {
            this.emit("dominantSpeaker", dominantSpeaker);
        });
        this[events_handler_1.addProxy](internalCall);
        internalCall.on("CALL_PRODUCER_KICKED", () => {
            this.emit("viewerKicked");
        });
        internalCall.on("CALL_PEER_AT_CAPACITY", (atCapacity) => {
            this.emit("peerAtCapacity", atCapacity);
        });
        internalCall.on("CALL_ACCESS_DENIED", (message) => {
            this.emit("accessDenied", message);
        });
        internalCall.on("CALL_PRODUCER_STATS", (producerStats) => {
            this.emit("producerStats", producerStats);
        });
        internalCall.on("CALL_CONNECTING", (value) => {
            this.emit("isConnecting", value);
        });
        internalCall.on("CALL_SET_CONNECTED", ({ connected }) => {
            this.emit("isConnected", connected);
        });
        internalCall.on("CALL_WEBSOCKET_RECONNECT", () => {
            this.emit("websocketReconnect");
        });
        internalCall.on("CALL_ENDED", () => {
            this.emit("callEnded");
        });
        return new Promise((resolve, reject) => {
            internalCall.start().catch((err) => {
                reject((0, errors_1.createError)(api_1.ErrorCode.UnableJoinCall, "Unable to start call", {}, err));
                internalCall.dispose(`Join call error: ${String(err)}`);
            });
            internalCall.once("CALL_READY", () => {
                resolve(internalCall);
            });
        });
    }
    get id() {
        var _a, _b, _c;
        return (_c = (_b = (_a = this.pvcCall) === null || _a === void 0 ? void 0 : _a.call) === null || _b === void 0 ? void 0 : _b.id) !== null && _c !== void 0 ? _c : "";
    }
    get peers() {
        return this.connectedPeers;
    }
    handleAddPeer({ peer }) {
        var _a, _b, _c, _d, _e, _f;
        if (this.pvcCall == null) {
            this.ctx.logger.warn("handleAddPeer() call is null", {
                aggregates: {
                    peerId: (_a = peer.peerId) !== null && _a !== void 0 ? _a : "",
                    displayName: (_b = peer.displayName) !== null && _b !== void 0 ? _b : "",
                    scope: (_c = peer.scope) !== null && _c !== void 0 ? _c : "",
                },
            });
            return;
        }
        if (this.connectedPeers.some((p) => p.peerId === peer.id)) {
            this.ctx.logger.debug("handleAddPeer() peer already exists", {
                aggregates: {
                    peerId: (_d = peer.peerId) !== null && _d !== void 0 ? _d : "",
                    displayName: (_e = peer.displayName) !== null && _e !== void 0 ? _e : "",
                    scope: (_f = peer.scope) !== null && _f !== void 0 ? _f : "",
                },
            });
            return;
        }
        const basePeer = new peer_1.Peer((0, logger_1.extendContext)(this.ctx, peer_1.Peer), this, this.pvcCall, peer, this.options.playerOptions);
        this.connectedPeers.push(basePeer);
        basePeer.on("videoConsumer", () => {
            this.emit("videoConsumer");
        });
        this[events_handler_1.addProxy](basePeer);
        this.ctx.logger.debug("handleAddPeer() peer added", basePeer);
        this.emit("peerAdded", { call: this, peer: basePeer });
    }
    handleRemovePeer({ peerId }) {
        const i = this.connectedPeers.findIndex((p) => p.peerId === peerId);
        if (i !== -1) {
            const deletingPeer = this.peers.splice(i, 1)[0];
            if (deletingPeer instanceof peer_1.Peer) {
                this.ctx.logger.debug("handleRemovePeer() peer removed", deletingPeer);
                deletingPeer.dispose("removing peer");
            }
            this.emit("peerRemoved", { call: this, peer: deletingPeer });
        }
    }
    async ready() {
        await this.readyPromise;
        return this;
    }
    async createCall(options) {
        var _a;
        if (this.callUrl == null) {
            this.throwErrorDeprecated(new errors_deprecated_1.ValidationError("callUrl is null", { critical: true }));
        }
        const body = {
            mode: "broadcast",
            region: (_a = this.region) !== null && _a !== void 0 ? _a : "",
            msVersion: [3],
        };
        Object.assign(body, options);
        this.ctx.logger.info("call creation", {
            callUrl: this.callUrl,
            auth: {
                token: this.token,
                hasRefreshToken: this.tokenRefresher != null,
            },
            body,
        });
        const response = await (0, request_1.default)(this.ctx, this.callUrl, {
            auth: this.auth,
            method: "post",
            body: JSON.stringify(body),
            failoverUrls: this.failoverCallUrls,
        });
        if (response != null) {
            return response.body;
        }
        this.ctx.logger.error("response is null", {
            callUrl: this.callUrl,
            auth: {
                token: this.token,
                hasRefreshToken: this.tokenRefresher != null,
            },
            ...body,
        });
        return null;
    }
    async createCallAndJoin(options, sfuOptions = {}) {
        var _a, _b;
        if (((_a = this.options) === null || _a === void 0 ? void 0 : _a.clientReferrer) != null) {
            options.clientReferrer = this.options.clientReferrer;
        }
        const createdCall = await this.createCall(options);
        const id = (_b = createdCall === null || createdCall === void 0 ? void 0 : createdCall.id) !== null && _b !== void 0 ? _b : "";
        return (0, call_1.default)(this.ctx, id, `${this.backendEndpoint}/lb/${callUrlVersion}/calls/${id}/join`, {
            auth: this.auth,
            failoverUrls: this.failover.map((lb) => `${lb}/lb/${callUrlVersion}/calls/${id}/join`),
            ...sfuOptions,
        }, this);
    }
    async joinCall(id, sfuJoinParams = null, sfuOptions = {}) {
        if (this.joinUrl == null) {
            this.throwErrorDeprecated(new errors_deprecated_1.ValidationError("joinUrl is null", {}));
        }
        // Allow the call to have access to environment
        const mergedOptions = {
            ...this.options.sfu,
            ...sfuOptions,
        };
        return (0, call_1.default)(this.ctx, id, this.joinUrl, {
            auth: this.auth,
            failoverUrls: this.failoverJoinUrls,
            ...mergedOptions,
        }, this, sfuJoinParams);
    }
    async broadcast(ctrl, broadcastOptions) {
        var _a;
        if (this.pvcCall == null) {
            this.throwErrorDeprecated(new errors_deprecated_1.InternalCallError("call is not started; pvcCall is null", {}));
        }
        if (this.pvcCall.streams[broadcastOptions.streamName] != null || this.broadcasts.has(broadcastOptions.streamName)) {
            this.throwErrorDeprecated(new errors_deprecated_1.StreamExistsError("stream name already exists", {
                broadcast: (_a = this.broadcasts.get(broadcastOptions.streamName)) !== null && _a !== void 0 ? _a : {},
                call: this,
            }));
        }
        this.ctx.logger.debug("starting broadcast", {
            hasAudio: ctrl.hasActiveAudioTrack(),
            hasVideo: ctrl.hasActiveVideoTrack(),
            broadcastOptions,
        });
        this.pvcCall.setStream(broadcastOptions.streamName, {
            audio: { streamProvider: ctrl },
            video: { streamProvider: ctrl },
        });
        const newBroadcast = new broadcast_1.Broadcast((0, logger_1.extendContext)(this.ctx, broadcast_1.Broadcast), this, this.pvcCall, ctrl, broadcastOptions);
        this[events_handler_1.addProxy](newBroadcast);
        /**
         * When the broadcast is disposed and closed, remove the stream
         * from the PVC call and remove the broadcast from `this.broadcasts`.
         */
        newBroadcast.once("disposed", () => {
            this.broadcasts.delete(broadcastOptions.streamName);
            this[events_handler_1.removeProxy](newBroadcast);
        });
        this.broadcasts.set(broadcastOptions.streamName, newBroadcast);
        return newBroadcast;
    }
    close(debugString = "Implementer did not pass debugString") {
        var _a, _b, _c;
        if (this.auth != null) {
            this.auth.destroy();
        }
        if (this.state !== "closed") {
            (_a = this.pvcCall) === null || _a === void 0 ? void 0 : _a.off("CALL_ADD_PEER", this.handleAddPeer);
            (_b = this.pvcCall) === null || _b === void 0 ? void 0 : _b.off("CALL_REMOVE_PEER", this.handleRemovePeer);
            try {
                (_c = this.pvcCall) === null || _c === void 0 ? void 0 : _c.close(debugString);
            }
            catch (err) {
                // pass
            }
            this.connectedPeers.splice(0, this.connectedPeers.length);
            (0, context_1.cancel)(this.ctx, "Call has been closed");
            this.emit("callClosed", { callId: this.id });
            this.state = "closed";
        }
    }
    async kickViewer(userId, streamOnly, webhook = false) {
        var _a;
        return (_a = this.pvcCall) === null || _a === void 0 ? void 0 : _a.kickViewer(userId, streamOnly, webhook);
    }
    async kickPeer(peerId, streamOnly, webhook) {
        var _a;
        return (_a = this.pvcCall) === null || _a === void 0 ? void 0 : _a.kickPeer(peerId, streamOnly, webhook);
    }
    // Permissions are type unknown for the time being, when this gets put into production we need to add a type.
    async promoteViewer(userId, permissions, webhook = false) {
        var _a;
        return (_a = this.pvcCall) === null || _a === void 0 ? void 0 : _a.promoteViewer(userId, permissions, webhook);
    }
    // Permissions are type unknown for the time being, when this gets put into production we need to add a type.
    async demoteViewer(userId, permissions, webhook = false) {
        var _a;
        return (_a = this.pvcCall) === null || _a === void 0 ? void 0 : _a.demoteViewer(userId, permissions, webhook);
    }
    toJSON() {
        var _a, _b, _c, _d;
        return {
            callUrl: this.callUrl,
            joinUrl: this.joinUrl,
            backendEndpoint: this.backendEndpoint,
            peersIds: this.connectedPeers.map((p) => p.peerId),
            options: {
                callId: (_a = this.options) === null || _a === void 0 ? void 0 : _a.callId,
                backendEndpoints: (_b = this.options) === null || _b === void 0 ? void 0 : _b.backendEndpoints,
                playerOptions: (_c = this.options) === null || _c === void 0 ? void 0 : _c.playerOptions,
                sfu: (_d = this.options) === null || _d === void 0 ? void 0 : _d.sfu,
            },
            aggregates: {
                ...(0, lib_1.extractAggregates)(this.pvcCall, "support"),
                support: this.ctx.support.hash,
                state: this.state,
            },
        };
    }
}
exports.Call = Call;
Call.displayName = "Call";
//# sourceMappingURL=call.js.map